
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [vagasRestantes, setVagasRestantes] = useState(23);

  // Countdown to event
  useEffect(() => {
    // 07/03/2026 às 14:00 - Lucas do Rio Verde - MT (fuso America/Cuiabá, UTC-4)
    const eventDate = new Date('2026-03-07T14:00:00-04:00');

    const calcTime = () => {
      const now = new Date();
      const diff = eventDate.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    calcTime();
    const timer = setInterval(calcTime, 1000);
    return () => clearInterval(timer);
  }, []);

  // Simulate spots decreasing over time
  useEffect(() => {
    const stored = sessionStorage.getItem('vagas_restantes');
    if (stored) {
      setVagasRestantes(parseInt(stored));
    } else {
      const base = 23;
      sessionStorage.setItem('vagas_restantes', String(base));
      setVagasRestantes(base);
    }

    const interval = setInterval(() => {
      setVagasRestantes(prev => {
        if (prev <= 5) return prev;
        const next = prev - 1;
        sessionStorage.setItem('vagas_restantes', String(next));
        return next;
      });
    }, 45000);

    return () => clearInterval(interval);
  }, []);

  // Scroll handler for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsNavScrolled(window.scrollY > 50);
    });
  }

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6gsi0apd4a8ta1lecu0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        // Dispara o evento Lead do Meta Pixel SOMENTE após envio confirmado com sucesso
        if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'Lead');
        }
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const scrollToForm = () => {
    document.getElementById('inscricao')?.scrollIntoView({ behavior: 'smooth' });
  };

  const openVideo = (videoId: string) => {
    window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isNavScrolled ? 'bg-black shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <img
            alt="Four Models"
            className="h-8 sm:h-10 md:h-12 object-contain"
            src="https://static.readdy.ai/image/103ec5ada18b00c7d95516c6aa3b7243/3c6853a00769ef0b898cf7c7be2b9fe6.png"
          />
          <button
            onClick={scrollToForm}
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-full font-bold text-xs sm:text-sm hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            INSCREVA-SE
          </button>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              alt="Fashion Background"
              className="w-full h-full object-cover object-top"
              src="https://public.readdy.ai/ai/img_res/b1e6f203c1c86f7f2e02323a22506a53.jpg"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/65 to-black/85"></div>
          </div>

          <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto w-full pt-24 sm:pt-20 pb-12">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
              <span className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-bold tracking-wider whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-calendar-event-fill text-sm"></i>
                07/03 • 14:00
              </span>
              <span className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-bold tracking-wider whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-map-pin-fill text-yellow-500 text-sm"></i>
                LUCAS DO RIO VERDE - MT
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 sm:mb-5 leading-tight">
              Sua Carreira na Moda, TV e
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                Redes Sociais Começa Aqui
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-2 sm:mb-3 font-light max-w-3xl mx-auto">
              Seletiva Oficial <strong className="text-white font-semibold">Four Models</strong> em{' '}
              <strong className="text-yellow-500 font-bold">Lucas do Rio Verde - MT</strong>
            </p>

            <p className="text-lg sm:text-xl md:text-2xl text-white mb-8 sm:mb-10 font-semibold max-w-2xl mx-auto leading-relaxed px-2">
              Descubra se você ou seu filho(a) tem perfil para moda, TV e internet.
            </p>

            <button
              onClick={scrollToForm}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 sm:px-10 md:px-14 py-4 sm:py-5 rounded-full text-base sm:text-lg md:text-xl font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/40 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              QUERO PARTICIPAR
            </button>

            <p className="text-yellow-500/80 text-xs sm:text-sm mt-3 sm:mt-4 font-medium">
              Inscrição gratuita • Não precisa experiência
            </p>

            <div className="mt-10 sm:mt-12 flex flex-wrap items-center justify-center gap-6 sm:gap-8 md:gap-12 text-white px-4">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-yellow-500">03–35</div>
                <div className="text-xs sm:text-sm text-gray-300 mt-1">Anos</div>
              </div>
              <div className="w-px h-10 sm:h-12 bg-gray-600"></div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-yellow-500">100%</div>
                <div className="text-xs sm:text-sm text-gray-300 mt-1">Gratuito</div>
              </div>
              <div className="w-px h-10 sm:h-12 bg-gray-600"></div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-yellow-500">0</div>
                <div className="text-xs sm:text-sm text-gray-300 mt-1">Experiência Necessária</div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-3xl text-yellow-500"></i>
          </div>
        </section>

        {/* Bloco de Escassez - fora da hero */}
        <section className="py-10 sm:py-12 bg-gray-950">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="bg-black/80 border border-yellow-500/30 rounded-2xl px-5 sm:px-8 py-6 sm:py-8">
              {/* Texto de escassez */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <i className="ri-fire-fill text-red-400 text-lg animate-pulse"></i>
                <p className="text-white font-black text-base sm:text-lg">
                  Restam apenas{' '}
                  <span className="text-red-400 text-xl sm:text-2xl">{vagasRestantes}</span>{' '}
                  vagas para esta cidade.
                </p>
                <i className="ri-fire-fill text-red-400 text-lg animate-pulse"></i>
              </div>

              {/* Barra visual de vagas */}
              <div className="flex justify-center gap-1 mb-2">
                {Array.from({ length: 30 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-5 sm:w-2.5 sm:h-6 rounded-sm transition-all duration-500 ${
                      i < vagasRestantes
                        ? i < 10
                          ? 'bg-red-500'
                          : i < 20
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                        : 'bg-white/10'
                    }`}
                  ></div>
                ))}
              </div>
              <p className="text-gray-400 text-xs text-center mb-6">
                {vagasRestantes} de 30 vagas disponíveis
              </p>

              {/* Divisor */}
              <div className="flex items-center gap-3 mb-5 max-w-xs mx-auto">
                <div className="flex-1 h-px bg-yellow-500/20"></div>
                <i className="ri-time-fill text-yellow-500/60 text-sm"></i>
                <div className="flex-1 h-px bg-yellow-500/20"></div>
              </div>

              {/* Contador regressivo */}
              <p className="text-gray-400 text-xs font-semibold tracking-widest uppercase text-center mb-4">
                O evento começa em
              </p>
              <div className="flex items-center justify-center gap-2 sm:gap-4">
                {[
                  { value: timeLeft.days, label: 'Dias' },
                  { value: timeLeft.hours, label: 'Horas' },
                  { value: timeLeft.minutes, label: 'Min' },
                  { value: timeLeft.seconds, label: 'Seg' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 sm:gap-4">
                    <div className="flex flex-col items-center">
                      <div className="bg-black/60 border border-yellow-500/30 rounded-xl w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center">
                        <span className="text-xl sm:text-2xl font-black text-yellow-400 tabular-nums">
                          {String(item.value).padStart(2, '0')}
                        </span>
                      </div>
                      <span className="text-gray-500 text-[10px] font-semibold mt-1.5 tracking-wider uppercase">
                        {item.label}
                      </span>
                    </div>
                    {idx < 3 && (
                      <span className="text-yellow-500/60 text-xl sm:text-2xl font-black mb-4 select-none">:</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-10 sm:py-12 bg-gradient-to-r from-yellow-500 to-yellow-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-40 h-40 bg-black rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-60 h-60 bg-black rounded-full translate-x-1/3 translate-y-1/3"></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
            <p className="text-black font-black text-xl sm:text-2xl md:text-3xl mb-2">Não precisa experiência.</p>
            <p className="text-black/80 text-sm sm:text-base md:text-lg mb-5 sm:mb-6 font-medium">
              Buscamos talentos naturais. Sua oportunidade começa Lucas do Rio Verde MT.
            </p>
            <button
              onClick={scrollToForm}
              className="bg-black text-yellow-500 px-8 sm:px-10 py-3 sm:py-4 rounded-full text-sm sm:text-base font-bold hover:bg-gray-900 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              INSCREVA-SE AGORA
            </button>
          </div>
        </section>

        {/* Quem Pode Participar */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-black mb-3 px-4">
                <a href="#quem-pode">
                  Quem Pode <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Participar</span>
                </a>
              </h4>
              <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto">
                Aberto para todos os perfis. Sem experiência necessária.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5 mb-8">
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-500 text-center group cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-bear-smile-line text-xl sm:text-2xl text-black"></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-black mb-1">Crianças</h3>
                <p className="text-sm text-yellow-600 font-semibold mb-1">03 a 12 anos</p>
                <p className="text-xs text-gray-500">Início promissor na carreira</p>
              </div>

              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-500 text-center group cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-user-smile-line text-xl sm:text-2xl text-black"></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-black mb-1">Adolescentes</h3>
                <p className="text-sm text-yellow-600 font-semibold mb-1">13 a 17 anos</p>
                <p className="text-xs text-gray-500">Fase ideal para começar</p>
              </div>

              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-500 text-center group cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-user-star-line text-xl sm:text-2xl text-black"></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-black mb-1">Jovens</h3>
                <p className="text-sm text-yellow-600 font-semibold mb-1">18 a 25 anos</p>
                <p className="text-xs text-gray-500">Auge para o mercado</p>
              </div>

              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-500 text-center group cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-user-heart-line text-xl sm:text-2xl text-black"></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-black mb-1">Adultos</h3>
                <p className="text-sm text-yellow-600 font-semibold mb-1">26 a 35 anos</p>
                <p className="text-xs text-gray-500">Maturidade e presença</p>
              </div>

              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-500 text-center group cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-camera-line text-xl sm:text-2xl text-black"></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-black mb-1">Influencers</h3>
                <p className="text-sm text-yellow-600 font-semibold mb-1">Todas as idades</p>
                <p className="text-xs text-gray-500">Potencial digital</p>
              </div>
            </div>

            <div className="bg-black rounded-2xl p-5 sm:p-7 text-center">
              <p className="text-lg sm:text-xl text-white font-bold">
                <span className="text-yellow-500">Zero experiência?</span> Sem problema. Avaliamos seu potencial natural.
              </p>
            </div>
          </div>
        </section>

        {/* Por Que Participar */}
        <section className="py-16 sm:py-20 bg-black">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 px-4">
                <a href="#beneficios">
                  Por Que <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Participar</span>
                </a>
              </h4>
              <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto">
                Mais do que uma seletiva. Uma porta para o mercado.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 sm:p-8 border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 group cursor-pointer">
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-12 h-12 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-user-search-line text-xl sm:text-2xl text-black"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5">Avaliação Profissional</h3>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                      Feedback de especialistas sobre seu potencial real no mercado.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 sm:p-8 border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 group cursor-pointer">
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-12 h-12 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-star-line text-xl sm:text-2xl text-black"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5">Oportunidade Real</h3>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                      Acesso direto a trabalhos em moda, publicidade e entretenimento.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 sm:p-8 border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 group cursor-pointer">
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-12 h-12 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-building-line text-xl sm:text-2xl text-black"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5">Ambiente Profissional</h3>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                      Seletiva real com estrutura completa e equipe qualificada.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 sm:p-8 border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 group cursor-pointer">
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-12 h-12 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-rocket-line text-xl sm:text-2xl text-black"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5">Desenvolvimento Artístico</h3>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                      Orientação profissional e networking para sua carreira.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Informações do Evento */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-black mb-3 px-4">
                <a href="#evento">
                  Informações do <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Evento</span>
                </a>
              </h4>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8">
              <div className="bg-white rounded-2xl p-5 sm:p-7 shadow-md text-center border border-gray-100 hover:border-yellow-500 transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-calendar-event-fill text-xl sm:text-2xl text-black"></i>
                </div>
                <p className="text-xs sm:text-sm font-bold text-gray-500 mb-1">Data e Horário</p>
                <p className="text-lg sm:text-xl font-black text-black">07/03</p>
                <p className="text-base sm:text-lg font-bold text-yellow-600">14:00</p>
              </div>

              <div className="bg-white rounded-2xl p-5 sm:p-7 shadow-md text-center border border-gray-100 hover:border-yellow-500 transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-2-fill text-xl sm:text-2xl text-black"></i>
                </div>
                <p className="text-xs sm:text-sm font-bold text-gray-500 mb-1">Local</p>
                <p className="text-lg sm:text-xl font-black text-black">Terras Hotel</p>
                <p className="text-base sm:text-lg font-bold text-yellow-600">Lucas do Rio Verde - MT</p>
              </div>

              <div className="bg-white rounded-2xl p-5 sm:p-7 shadow-md text-center border border-gray-100 hover:border-yellow-500 transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-group-fill text-xl sm:text-2xl text-black"></i>
                </div>
                <p className="text-xs sm:text-sm font-bold text-gray-500 mb-1">Formato</p>
                <p className="text-lg sm:text-xl font-black text-black">Presencial</p>
              </div>

              <div className="bg-white rounded-2xl p-5 sm:p-7 shadow-md text-center border border-gray-100 hover:border-yellow-500 transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-alarm-warning-fill text-xl sm:text-2xl text-black"></i>
                </div>
                <p className="text-xs sm:text-sm font-bold text-gray-500 mb-1">Vagas</p>
                <p className="text-lg sm:text-xl font-black text-black">Limitadas</p>
                <p className="text-base sm:text-lg font-bold text-yellow-600">Por horário</p>
              </div>
            </div>
          </div>
        </section>

        {/* Quem Está Por Trás */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-3xl pointer-events-none"></div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center mb-10 sm:mb-14">
              <p className="text-xs sm:text-sm font-bold text-yellow-600 tracking-widest uppercase mb-3">
                Tradição · Credibilidade · Resultados
              </p>
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-black mb-3 px-4">
                <a href="#autoridade">
                  Quem Está Por Trás da{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Four Models?</span>
                </a>
              </h4>
              <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto">
                Mais de três décadas transformando vidas e lançando carreiras no mercado da moda e do entretenimento.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-10 sm:mb-14">
              <div className="bg-black rounded-2xl p-5 sm:p-7 text-center border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 cursor-pointer group">
                <div className="w-11 h-11 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-trophy-fill text-lg sm:text-xl text-black"></i>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-yellow-500 mb-1">38+</p>
                <p className="text-gray-400 text-xs sm:text-sm">Anos de mercado</p>
              </div>

              <div className="bg-black rounded-2xl p-5 sm:p-7 text-center border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 cursor-pointer group">
                <div className="w-11 h-11 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-global-fill text-lg sm:text-xl text-black"></i>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-yellow-500 mb-1">100+</p>
                <p className="text-gray-400 text-xs sm:text-sm">Modelos no mercado internacional</p>
              </div>

              <div className="bg-black rounded-2xl p-5 sm:p-7 text-center border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 cursor-pointer group">
                <div className="w-11 h-11 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-star-fill text-lg sm:text-xl text-black"></i>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-yellow-500 mb-1">1ª</p>
                <p className="text-gray-400 text-xs sm:text-sm">Top model brasileira lançada</p>
              </div>

              <div className="bg-black rounded-2xl p-5 sm:p-7 text-center border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 cursor-pointer group">
                <div className="w-11 h-11 sm:w-13 sm:h-13 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-film-fill text-lg sm:text-xl text-black"></i>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-yellow-500 mb-1">TV & Moda</p>
                <p className="text-gray-400 text-xs sm:text-sm">Atores e modelos nas maiores emissoras</p>
              </div>
            </div>

            <div className="bg-black rounded-2xl p-6 sm:p-8 md:p-10 border border-yellow-500/20 mb-8 sm:mb-10">
              <div className="space-y-4 sm:space-y-5">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-award-fill text-base sm:text-lg text-black"></i>
                  </div>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Dirigida por Marla Drebes, referência nacional e internacional na descoberta de talentos.
                  </p>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-book-fill text-base sm:text-lg text-black"></i>
                  </div>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Marla Drebes está no RankBrasil em 2004, é a Primeira brasileira a descobrir e projetar uma top model made in Brasil: Shirley Mallmann foi lançada em janeiro de 1995.
                  </p>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-user-star-fill text-base sm:text-lg text-black"></i>
                  </div>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Responsável por lançar Shirley Mallmann — a primeira top model brasileira no cenário internacional.
                  </p>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-flashlight-fill text-base sm:text-lg text-black"></i>
                  </div>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Revelou talentos como Jeisa Chiminazzo e diversos nomes que atuam no Brasil e no exterior.
                  </p>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-movie-2-fill text-base sm:text-lg text-black"></i>
                  </div>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Trabalha também com atores, conectando talentos a testes nas maiores emissoras do país.
                  </p>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-medal-fill text-base sm:text-lg text-black"></i>
                  </div>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Histórico de grandes projetos e recordes nacionais no segmento da moda.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={scrollToForm}
                type="button"
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-xl hover:shadow-yellow-500/30 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                GARANTIR MINHA VAGA
              </button>
            </div>
          </div>
        </section>

        {/* Histórias de Sucesso */}
        <section className="py-16 sm:py-20 bg-black relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img alt="Background" className="w-full h-full object-cover object-top" src="https://www.sorriso.fourmodelsseletivas.com.br/static/media/bg-testimonials.jpg" />
          </div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center mb-10 sm:mb-14">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 px-4">
                <a href="#historias">
                  Histórias de <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Sucesso</span>
                </a>
              </h4>
              <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto">
                Assista os depoimentos de quem já passou pelas nossas seletivas.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mb-8 sm:mb-10">
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-yellow-500/20 flex flex-col">
                <div className="relative w-full aspect-video bg-black">
                  <div onClick={() => openVideo('fWZeK74x6Us')} className="absolute inset-0 w-full h-full cursor-pointer group">
                    <img
                      alt="Depoimento de Taís Pinheiro"
                      className="w-full h-full object-cover object-top"
                      src="https://img.youtube.com/vi/fWZeK74x6Us/hqdefault.jpg"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-yellow-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-yellow-500/30">
                        <i className="ri-play-fill text-black text-2xl sm:text-3xl ml-1"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5 sm:p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-base sm:text-lg text-yellow-500"></i>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-auto">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-base">T</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Taís Pinheiro</p>
                      <p className="text-gray-500 text-xs">Sinop - MT • Modelo Internacional</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-yellow-500/20 flex flex-col">
                <div className="relative w-full aspect-video bg-black">
                  <div onClick={() => openVideo('GEI7zFUsukk')} className="absolute inset-0 w-full h-full cursor-pointer group">
                    <img
                      alt="Depoimento de Beatriz Zachert"
                      className="w-full h-full object-cover object-top"
                      src="https://img.youtube.com/vi/GEI7zFUsukk/hqdefault.jpg"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-yellow-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-yellow-500/30">
                        <i className="ri-play-fill text-black text-2xl sm:text-3xl ml-1"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5 sm:p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-base sm:text-lg text-yellow-500"></i>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-auto">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-base">B</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Beatriz Zachert</p>
                      <p className="text-gray-500 text-xs">Sorriso - MT • Modelo Internacional</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-yellow-500/20 flex flex-col">
                <div className="relative w-full aspect-video bg-black">
                  <div onClick={() => openVideo('Xvk5lA_x5ic')} className="absolute inset-0 w-full h-full cursor-pointer group">
                    <img
                      alt="Depoimento de Douglas Malmman"
                      className="w-full h-full object-cover object-top"
                      src="https://img.youtube.com/vi/Xvk5lA_x5ic/hqdefault.jpg"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-yellow-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-yellow-500/30">
                        <i className="ri-play-fill text-black text-2xl sm:text-3xl ml-1"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5 sm:p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-base sm:text-lg text-yellow-500"></i>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-auto">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-base">D</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Douglas Malmman</p>
                      <p className="text-gray-500 text-xs">CEO da Mädchen Models • Modelo Internacional</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-yellow-500/20 flex flex-col">
                <div className="relative w-full aspect-video bg-black">
                  <div onClick={() => openVideo('zLSADgvoVXI')} className="absolute inset-0 w-full h-full cursor-pointer group">
                    <img
                      alt="Depoimento de Valentina Marques"
                      className="w-full h-full object-cover object-top"
                      src="https://img.youtube.com/vi/zLSADgvoVXI/hqdefault.jpg"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-yellow-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-yellow-500/30">
                        <i className="ri-play-fill text-black text-2xl sm:text-3xl ml-1"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5 sm:p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-base sm:text-lg text-yellow-500"></i>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-auto">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-base">V</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Valentina Marques</p>
                      <p className="text-gray-500 text-xs">Presidente Figueiredo - AM • Atriz</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5 text-center">
                <p className="text-2xl sm:text-3xl font-black text-yellow-500 mb-1">+50.000</p>
                <p className="text-gray-400 text-xs sm:text-sm">Participantes em seletivas</p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5 text-center">
                <p className="text-2xl sm:text-3xl font-black text-yellow-500 mb-1">+1.000</p>
                <p className="text-gray-400 text-xs sm:text-sm">Talentos descobertos</p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5 text-center">
                <p className="text-2xl sm:text-3xl font-black text-yellow-500 mb-1">Brasil</p>
                <p className="text-gray-400 text-xs sm:text-sm">Inteiro atendido</p>
              </div>
            </div>
          </div>
        </section>

        {/* Etapa Final - Léo Belmonte */}
        <section className="py-20 sm:py-28 relative overflow-hidden bg-gradient-to-br from-gray-950 via-[#0a0a1a] to-gray-950">
          <div className="absolute top-0 left-0 w-72 h-72 bg-yellow-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-500/3 rounded-full blur-[120px]"></div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center mb-12 sm:mb-16">
              <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-5 py-2 mb-6">
                <i className="ri-star-fill text-yellow-500 text-sm"></i>
                <span className="text-yellow-500 text-xs font-semibold tracking-widest uppercase">
                  Participação Especial Confirmada
                </span>
                <i className="ri-star-fill text-yellow-500 text-sm"></i>
              </div>
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-white">
                <a href="#participacao-especial">
                  Etapa <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">Final</span>
                </a>
              </h4>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-3xl blur-2xl"></div>
                  <div className="relative w-[320px] h-[420px] sm:w-[380px] sm:h-[480px] rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-yellow-500/20">
                    <img
                      alt="Léo Belmonte - Ator brasileiro conhecido por Carrossel SBT e 3% Netflix"
                      title="Léo Belmonte ator participação especial Four Models Lucas do Rio Verde"
                      className="w-full h-full object-cover object-top"
                      src="https://static.readdy.ai/image/103ec5ada18b00c7d95516c6aa3b7243/bcdca41a21e27164614ffcdeaee4e2ad.jpeg"
                    />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent h-32"></div>
                  </div>
                  <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/30 rotate-6">
                    <i className="ri-clapperboard-fill text-black text-xl"></i>
                  </div>
                </div>
              </div>

              <div className="text-center lg:text-left">
                <div className="flex items-center gap-2 justify-center lg:justify-start mb-2">
                  <div className="w-8 h-[2px] bg-yellow-500"></div>
                  <span className="text-yellow-500 text-xs font-semibold tracking-widest uppercase">Convidado Especial</span>
                </div>
                <h3 className="text-3xl sm:text-4xl md:text-5xl font-black mb-2">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600">
                    Léo Belmonte
                  </span>
                </h3>
                <p className="text-gray-400 text-sm sm:text-base font-medium mb-8 flex items-center justify-center gap-2 justify-start">
                  <i className="ri-film-fill text-yellow-500/70 text-base"></i>
                  Ator • Carrossel (SBT) • 3% (Netflix)
                </p>

                <div className="space-y-5 mb-10">
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Na etapa final da <strong className="text-white">Convenção Four Models</strong>, contaremos com a participação especial do ator{' '}
                    <strong className="text-white">Léo Belmonte</strong>, conhecido por interpretar{' '}
                    <strong className="text-white">Jorge na novela Carrossel (SBT)</strong>, além de participação na série{' '}
                    <strong className="text-white">3% da Netflix</strong> e outros trabalhos no audiovisual brasileiro.
                  </p>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Sua presença reforça o compromisso da <strong className="text-white">Four Models</strong> em proporcionar{' '}
                    <strong className="text-yellow-500">experiência real de mercado</strong>, conexão com profissionais atuantes e vivência prática no cenário artístico.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                    <div className="w-9 h-9 bg-yellow-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <i className="ri-tv-fill text-yellow-500 text-base"></i>
                    </div>
                    <p className="text-white text-xs font-semibold">Carrossel</p>
                    <p className="text-gray-500 text-[11px]">SBT</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                    <div className="w-9 h-9 bg-yellow-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <i className="ri-netflix-fill text-yellow-500 text-base"></i>
                    </div>
                    <p className="text-white text-xs font-semibold">3%</p>
                    <p className="text-gray-500 text-[11px]">Netflix</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                    <div className="w-9 h-9 bg-yellow-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <i className="ri-movie-2-fill text-yellow-500 text-base"></i>
                    </div>
                    <p className="text-white text-xs font-semibold">Audiovisual</p>
                    <p className="text-gray-500 text-[11px]">Brasileiro</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Formulário de Inscrição */}
        <section id="inscricao" className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-10">
              <h4 className="text-3xl sm:text-4xl md:text-5xl font-black text-black mb-3 px-4">
                <a href="#inscricao">
                  Garanta sua <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">vaga gratuita</span>
                </a>
              </h4>
              <p className="text-sm sm:text-base text-gray-500">Leva menos de 30 segundos para se inscrever.</p>
            </div>

            {formStatus === 'success' ? (
              <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10 border-2 border-yellow-400 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg shadow-yellow-500/30">
                  <i className="ri-check-line text-3xl text-black font-black"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-black mb-2">Inscrição Confirmada!</h3>
                <p className="text-gray-500 text-sm sm:text-base mb-7">
                  Entraremos em contato pelo WhatsApp com os detalhes.
                </p>

                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-5 sm:p-6 mb-7 text-left">
                  <p className="text-black font-black text-base sm:text-lg mb-4 flex items-center gap-2">
                    <i className="ri-map-pin-fill text-red-500 text-lg"></i>
                    Nos vemos em Lucas do Rio Verde - MT!
                  </p>
                  <div className="space-y-2 text-sm sm:text-base text-gray-700">
                    <p><strong>Data:</strong> 07/03 • Sexta-Feira</p>
                    <p><strong>Horário:</strong> 14:00</p>
                    <p><strong>Local:</strong> Terras Hotel</p>
                    <p><strong>Endereço:</strong> Lucas do Rio Verde – MT</p>
                  </div>
                </div>

                <button
                  onClick={() => setFormStatus('idle')}
                  className="text-yellow-600 font-semibold text-sm underline underline-offset-2 hover:text-yellow-700 transition-colors cursor-pointer"
                >
                  Fazer nova inscrição
                </button>
              </div>
            ) : (
              <form
                id="form-inscricao"
                data-readdy-form
                onSubmit={handleFormSubmit}
                className="bg-white rounded-2xl shadow-2xl p-7 sm:p-10 border-2 border-gray-100"
              >
                <div className="space-y-5 sm:space-y-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-bold text-gray-700 mb-2">
                      Nome Completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="nome"
                      name="nome"
                      required
                      className="w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 border-gray-200 focus:border-yellow-500 placeholder-gray-400"
                      placeholder="Seu nome completo"
                    />
                  </div>

                  <div>
                    <label htmlFor="idade" className="block text-sm font-bold text-gray-700 mb-2">
                      Idade <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="idade"
                      name="idade"
                      required
                      type="number"
                      inputMode="numeric"
                      min={3}
                      max={99}
                      onInput={(e) => {
                        const val = parseInt((e.target as HTMLInputElement).value);
                        if (val < 3) (e.target as HTMLInputElement).setCustomValidity('Idade mínima é 3 anos.');
                        else if (val > 99) (e.target as HTMLInputElement).setCustomValidity('Idade máxima é 99 anos.');
                        else (e.target as HTMLInputElement).setCustomValidity('');
                      }}
                      className="w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 border-gray-200 focus:border-yellow-500 placeholder-gray-400"
                      placeholder="Sua idade (entre 3 e 99 anos)"
                    />
                  </div>

                  <div>
                    <label htmlFor="whatsapp" className="block text-sm font-bold text-gray-700 mb-2">
                      WhatsApp <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="whatsapp"
                      name="whatsapp"
                      required
                      type="tel"
                      pattern="[\d\s\(\)\-\+]{8,20}"
                      onInput={(e) => {
                        const val = (e.target as HTMLInputElement).value.replace(/\D/g, '');
                        if (val.length < 8) (e.target as HTMLInputElement).setCustomValidity('Informe um número de WhatsApp válido.');
                        else (e.target as HTMLInputElement).setCustomValidity('');
                      }}
                      className="w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 border-gray-200 focus:border-yellow-500 placeholder-gray-400"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label htmlFor="cidade" className="block text-sm font-bold text-gray-700 mb-2">
                      Cidade <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="cidade"
                      name="cidade"
                      required
                      className="w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 border-gray-200 focus:border-yellow-500 placeholder-gray-400"
                      placeholder="Ex: Lucas do Rio Verde - MT"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full mt-8 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-6 py-4 sm:py-5 rounded-full text-sm sm:text-base font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap tracking-wide"
                >
                  {formStatus === 'submitting' ? 'ENVIANDO...' : 'GARANTIR MINHA VAGA GRATUITA'}
                </button>

                {formStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-center">
                    <p className="text-red-700 font-semibold text-sm">Erro ao enviar. Tente novamente.</p>
                  </div>
                )}

                <p className="text-center text-xs text-gray-400 mt-4">Inscrição 100% gratuita • Seus dados estão seguros</p>
              </form>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center justify-center gap-5 sm:gap-6 mb-6 sm:mb-8">
            <img
              alt="Four Models"
              className="h-14 sm:h-18 md:h-20 object-contain"
              src="https://static.readdy.ai/image/103ec5ada18b00c7d95516c6aa3b7243/f6ba88513f846dcaf1ba402d9a75d603.png"
            />
            <div className="text-center">
              <p className="text-white font-bold text-base sm:text-lg mb-1">Seletiva Presencial Four Models</p>
              <p className="text-gray-400 text-sm">07 de Março às 14:00</p>
              <p className="text-gray-400 text-sm">Terras Hotel</p>
              <p className="text-gray-400 text-sm">Lucas do Rio Verde – MT</p>
            </div>
            <div className="flex items-center gap-5 text-gray-400">
              <a
                href="https://www.instagram.com/fourmodelsoficial/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="hover:text-yellow-500 transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a
                href="https://www.facebook.com/fourmodelsoficial"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="hover:text-yellow-500 transition-colors cursor-pointer"
              >
                <i className="ri-facebook-circle-line text-xl"></i>
              </a>
              <a
                href="https://whatsa.me/5551998977297/?t=Tenho%20d%C3%BAvidas%20sobre%20a%20sele%C3%A7%C3%A3o%20de%20Lucas%20do%20Rio%20Verde%20-%20MT%20e%20gostaria%20de%20mais%20informa%C3%A7oes!"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="hover:text-yellow-500 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
              </a>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-5 text-center">
            <p className="text-gray-500 text-xs">
              © 2025 Four Models. Todos os direitos reservados. •{' '}
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="hover:text-yellow-500 transition-colors font-semibold cursor-pointer"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
